<template>
  <div style="width: 100%;">
    <div>
      <v-row>
        <v-col cols="12" md="2" class="left-panel">
          <div class="logo-container">
            <img src="../../assets/logo-cheque-intermittents-vector.svg" alt="Logo" class="logo"/>
          </div>

          <div>
            <ul class="step-list">
              <li
                  v-for="(step, index) in steps"
                  :key="index"
                  :class="{ active: index <= currentStep }"
              >
                <div class="step-item">
                  <span class="step-number">{{ index + 1 }}</span>
                  <span class="step-label">{{ step }}</span>
                </div>
                <div class="step-line" v-if="index < steps.length - 1"></div>
              </li>
            </ul>
          </div>
        </v-col>

        <v-col cols="12" md="10" class="right-panel">
          <div class="right-panel-inner">
            <div class="main-content">
              <component :is="sectionComponent" ref="SECTION_DIALOG" v-bind="currentProperties"
                         v-on="events"></component>
            </div>

            <div v-if="currentStep == 4">
              <span class="textParamConnextion">{{ textParamConnexion }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts">
import {isMobile} from "@/style/StyleHelpers.ts";
import SiretInscription from '@/generated/classes/SiretInscription.ts';
import {storeEtablissement, storeUser} from '@/generated/stores';
import loaderButton from '@/framework/base/components/LoaderButton.vue';
import {Sections} from '@/generated/classes/AppSchema.ts';
import {type Component, defineComponent} from "vue";
import {useRoute, useRouter} from "vue-router";
import {ConnectionParamModes} from "@/generated/classes/Session.ts";
import {AppCustoms, RessourceAppCustom} from "@/ressources/globalRessources.ts";
import SectionContainer from "@/framework/base/components/SectionContainer.vue";
import SectionRegisterNomPrenom from "@/components/Register/SectionRegisterNomPrenom.vue";
import SectionRegisterVerifMail from "@/components/Register/SectionRegisterVerifMail.vue";
import SectionRegisterVerifNumero from "@/components/Register/SectionRegisterVerifNumero.vue";
import SectionRegisterActivite from "@/components/Register/SectionRegisterActivite.vue";
import SectionRegisterInfoEmployeur from "@/components/Register/SectionRegisterInfoEmployeur.vue";
import SectionRegisterModeConnexion from "@/components/Register/SectionRegisterModeConnexion.vue";

export default defineComponent({
  name: 'SectionRegister',
  components: {
    SectionContainer,
    loaderButton,
  },
  props: {
    siteWeb: {
      type: String
    },
    identifiant: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isMobile: isMobile(),
      subscription: new SiretInscription(),
      currentStep: 0,
    };
  },
  computed: {
    textParamConnexion(): string {
      return RessourceAppCustom(AppCustoms.REGISTER_TEXT_INFO_PARAM_CONNEXION).label;
    },
    fromSection(): Sections {
      return Sections.CONNEXION;
    },
    steps(): string[] {
      const stepsList = [
        'Prénom Nom',
        'Information de contact',
        'Votre activité',
        'Informations employeur',
        'Préférence de connexion'
      ];
      return stepsList;
    },
    sectionComponent(): Component | null {
      if (this.currentStep == 0) {
        return SectionRegisterNomPrenom;
      } else if (this.currentStep == 1 && !this.subscription.verifyMail) {
        return SectionRegisterVerifMail;
      } else if (this.currentStep == 1 && this.subscription.verifyMail && !this.subscription.verifyNumero) {
        return SectionRegisterVerifNumero;
      } else if (this.currentStep == 2) {
        return SectionRegisterActivite;
      } else if (this.currentStep == 3) {
        return SectionRegisterInfoEmployeur;
      } else if (this.currentStep == 4) {
        return SectionRegisterModeConnexion;
      } else {
        return null;
      }
    },
    currentProperties (): any {
      return {
        "subscription": this.subscription,
        "user": this.subscription.user,
        "from-section": this.fromSection,
        "etablissement": this.subscription.etablissement
      };
    },
    events (): Record<string, () => void> {
      const event: any = {};
      event["next"] = () => this.next();
      event["previous"] = () => this.previous();
      return event;
    },
  },
  
  created(): void {
    this.subscription.isMobile = this.isMobile;
    this.subscription.user = storeUser.createNewUser();
    this.subscription.user.identifiant = this.identifiant;
    this.subscription.user.connectionMode = ConnectionParamModes.CODE;
    this.subscription.etablissement = storeEtablissement.createNewEtablissement();
  },

  methods: {
    next(): void {
      this.currentStep += 1;
    },
    previous(): void {
      if (this.currentStep === 0) {
        this.$emit('toggleForm');
      } else {
        this.currentStep -= 1;
      }
    },
  }
})
</script>
<style>

.subscribe-etab-identite .select-field {
  padding-top: 0 !important;
}

.left-panel {
  background-color: rgb(var(--v-theme-primary100));
  box-sizing: border-box;
  padding: 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.right-panel {
  box-sizing: border-box;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 100vh;
  background-color: rgb(var(--v-theme-grey97));
  position: relative;
}

.right-panel-inner {
  position: relative;
  height: 100%;
}

.main-content {
  flex-grow: 1;
  margin-top: 50px;
  padding-left: 80px;
  width: 800px;
}

.logo-container {
  margin-top: 50px;
  display: flex;
  justify-content: center;
}

.logo {
  object-fit: contain;
  width: 140px;
  height: 38px;
}

.step-list {
  margin-top: 100px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 200px;
  margin-left: auto;
  margin-right: auto;
}

.step-list li {
  display: flex;
  align-items: start;
  flex-shrink: 0;
  position: relative;
  flex-direction: column;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
}

.step-number {
  background-color: rgb(var(--v-theme-grey95));
  border-radius: 8px;
  gap: 8px;
  padding: 8px;
  color: rgb(var(--v-theme-secondary50));
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
  font-weight: 500;
}

.step-label {
  font-size: 12px;
  color: rgb(var(--v-theme-secondary50));
  white-space: nowrap;
  font-weight: 400;
}

.step-list li.active .step-label {
  font-weight: 500;
  color: #171A1C;
}

.step-list li.active .step-number {
  font-weight: 500;
  color: rgb(var(--v-theme-grey10));
}

.step-line {
  width: 2px;
  height: 32px;
  background-color: rgb(var(--v-theme-grey80));
  margin-left: 11px;
}

.disabled-section {
  opacity: 0.3;
  pointer-events: none;
  user-select: none;
}

.gradient-ia {
  background: linear-gradient(to right, rgb(var(--v-theme-information10)), rgb(var(--v-theme-information50)));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: bold;
}

.textParamConnextion {
  padding-left: 80px;
  margin-bottom: 80px;
  font-weight: 400;
  font-size: 12px;
  color: rgb(var(--v-theme-secondary50));
}


</style>
